import { useCallback, useState } from 'react';
import { companyRequest } from '@/company/api';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceConfirmCopy from '@/i18n/ns/voice-confirm';

/** A persisted connected-service action receipt, as the action routes return it. */
export type ConnectionAction = {
  id: string;
  connectionName: string;
  tool: string;
  title: string;
  arguments: Record<string, unknown>;
  state: 'pending' | 'running' | 'executed' | 'rejected' | 'failed' | 'unknown';
  result?: unknown;
  error?: string;
  createdAt: string;
};

export function useConnectionAction(
  endpoint = '/api/v1/company/connection-actions',
  onChanged?: () => void,
  failureMessage?: string,
) {
  const copy = useNamespaceCopy(voiceConfirmCopy);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const decide = useCallback(
    async (item: ConnectionAction, decision: 'approve' | 'reject') => {
      if (item.state !== 'pending' || busy) return { ok: false as const };
      setBusy(item.id);
      setError(null);
      try {
        const result = await companyRequest<{ action: ConnectionAction }>(
          `${endpoint}/${encodeURIComponent(item.id)}/${decision}`,
          'POST',
          {},
        );
        onChanged?.();
        return { ok: true as const, action: result.action };
      } catch {
        setError(failureMessage ?? copy.unknownCheckService);
        return { ok: false as const };
      } finally {
        setBusy(null);
      }
    },
    [busy, copy.unknownCheckService, endpoint, failureMessage, onChanged],
  );
  return { busy, error, setError, decide };
}
